import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pause, Play, ArrowLeft, Trophy, Wifi, WifiOff, Smartphone } from "lucide-react";
import TopBar from "@/components/TopBar";
import { sfx } from "@/lib/sounds";
import { createRoom, wsBase } from "@/lib/api";
import ChatPanel from "@/components/ChatPanel";
import { POWERUP_DEFS, drawPowerupIcon, drawBongPipe } from "@/lib/powerups";
import { drawArenaBackground, drawPaddle, drawBall } from "@/lib/arena";
import { resolveAvatarUrl } from "@/lib/avatars";
import { DaggerInWoodIcon } from "@/components/MedievalIcons";

const W = 1000;
const H = 600;

const DIFFICULTY_CONFIG = {
  easy: { ballSpeed: 6.5, paddleSpeed: 8.5, winScore: 5, powerInterval: 3000 },
  medium: { ballSpeed: 8.5, paddleSpeed: 9.5, winScore: 7, powerInterval: 4000 },
  hard: { ballSpeed: 11, paddleSpeed: 10.5, winScore: 11, powerInterval: 5000 },
};

const PADDLE_W = 14;
const BASE_PADDLE_H = 110;
const POWERUP_R = 20;
const POWERUP_TYPES = ["slow", "fast"];
const MAX_BALLS = 3;
// Bong-pipe periodic event: a glass bong pipe appears on either player's
// side, accompanied by a gurgling sound.  Tunables:
const BONG_PIPE_INTERVAL_MS = 12000; // base interval between appearances
const BONG_PIPE_VISIBLE_MS = 4000;   // how long the pipe stays on screen

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const makeBall = (x, y, vx, vy, speed, scoring = true) => ({
  x, y, vx, vy, r: 9, speed, scoring,
});

const PongGame = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const difficulty = (params.get("difficulty") || "medium").toLowerCase();
  const cfg = DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG.medium;
  const p1Name = (params.get("p1") || "Player 1").slice(0, 20);
  const p1Avatar = (params.get("avatar") || "").slice(0, 60);

  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const keysRef = useRef({});
  const p2DirRef = useRef(0); // -1 up, 1 down, 0 stop
  const rafRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const wsRef = useRef(null);
  const lastStreamRef = useRef(0);
  const lobbyBottomRef = useRef(null);
  const outboundQueueRef = useRef([]);

  const flushOutbound = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const q = outboundQueueRef.current;
    while (q.length) {
      const payload = q.shift();
      try { ws.send(JSON.stringify(payload)); } catch { q.unshift(payload); break; }
    }
  }, []);

  const [score, setScore] = useState({ p1: 0, p2: 0 });
  const [paused, setPaused] = useState(false);
  const [winner, setWinner] = useState(null);
  const [scorePulse, setScorePulse] = useState({ p1: 0, p2: 0 });

  // Room / connection state
  const [roomCode, setRoomCode] = useState(null);
  const [roomError, setRoomError] = useState(null);
  const [p2Name, setP2Name] = useState(null);
  const [p2Avatar, setP2Avatar] = useState("");
  const [controllerConnected, setControllerConnected] = useState(false);
  const [matchStarted, setMatchStarted] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);

  if (stateRef.current === null) {
    const startBongPipeOffset = BONG_PIPE_INTERVAL_MS * 1.5; // first pipe appears ~18s into a match
    stateRef.current = {
      balls: [makeBall(W / 2, H / 2, cfg.ballSpeed, cfg.ballSpeed * 0.5, cfg.ballSpeed, true)],
      p1: { x: 30, y: H / 2 - BASE_PADDLE_H / 2, h: BASE_PADDLE_H, w: PADDLE_W, score: 0, expiresAt: 0, aegis: false },
      p2: { x: W - 30 - PADDLE_W, y: H / 2 - BASE_PADDLE_H / 2, h: BASE_PADDLE_H, w: PADDLE_W, score: 0, expiresAt: 0, aegis: false },
      lastHitter: null,
      powerup: null,
      nextPowerupAt: Date.now() + cfg.powerInterval,
      // Bong-pipe periodic event state.
      bongPipe: null, // { side: "p1"|"p2", x, y, until }
      nextBongPipeAt: Date.now() + startBongPipeOffset,
      paused: true,
      finished: false,
      running: false,
    };
  }

  const wsSend = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(obj)); } catch {}
    }
  }, []);

  const wsSendChat = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)); } catch { outboundQueueRef.current.push(payload); }
    } else {
      outboundQueueRef.current.push(payload);
    }
  }, []);

  const resetBalls = useCallback((toLoserSide) => {
    const s = stateRef.current;
    const dirX = toLoserSide === "p1" ? -1 : 1;
    const speed = cfg.ballSpeed;
    const angle = (Math.random() * 0.6 - 0.3);
    s.balls = [makeBall(
      W / 2, H / 2,
      Math.cos(angle) * speed * dirX,
      Math.sin(angle) * speed,
      speed,
      true
    )];
    s.lastHitter = null;
    s.bongPipe = null;
  }, [cfg.ballSpeed]);

  const spawnPowerup = useCallback(() => {
    const s = stateRef.current;
    // "Surprise drop" — buffs and debuffs are treated uniformly.  We
    // pick a random effect type, spawn the drop exactly at the vertical
    // centerline of the table, and give it a constant horizontal
    // velocity left or right (direction randomly chosen).
    const type = POWERUP_TYPES[Math.floor(Math.random() * POWERUP_TYPES.length)];
    const dir = Math.random() < 0.5 ? -1 : 1;
    const drift = (cfg.ballSpeed * 0.55) * dir; // moves slower than the ball
    s.powerup = {
      x: W / 2,
      y: 60 + Math.random() * (H - 120),
      vx: drift,
      vy: 0,
      type,
      spawned: Date.now(),
    };
  }, [cfg.ballSpeed]);

  const applyPowerup = useCallback((type, hitter) => {
    const s = stateRef.current;
    sfx.powerup();
    const now = Date.now();
    if (type === "fast" || type === "speed") {
      // Bag of white powder — make the ball FAST.
      s.balls.forEach((b) => {
        b.speed = Math.min(b.speed * 1.45, cfg.ballSpeed * 2.6);
        const cur = Math.hypot(b.vx, b.vy) || 1;
        b.vx = (b.vx / cur) * b.speed;
        b.vy = (b.vy / cur) * b.speed;
      });
    } else if (type === "slow") {
      // Syringe — make the ball slower (but never so slow it looks frozen).
      s.balls.forEach((b) => {
        b.speed = Math.max(b.speed * 0.65, cfg.ballSpeed * 0.65);
        const cur = Math.hypot(b.vx, b.vy) || 1;
        b.vx = (b.vx / cur) * b.speed;
        b.vy = (b.vy / cur) * b.speed;
      });
    }
    s.powerup = null;
    s.nextPowerupAt = now + cfg.powerInterval;
  }, [cfg.ballSpeed, cfg.powerInterval]);

  const finishMatch = useCallback(async (winnerSide, p2NameLocal) => {
    const s = stateRef.current;
    s.finished = true;
    s.running = false;
    sfx.win();
    const isP1 = winnerSide === "p1";
    const winnerName = isP1 ? p1Name : (p2NameLocal || "Player 2");
    const winnerPts = isP1 ? s.p1.score : s.p2.score;
    const loserPts  = isP1 ? s.p2.score : s.p1.score;
    const computedScore = loserPts <= 0
      ? Math.round(1_000_000 * winnerPts)
      : Math.round((1_000_000 * winnerPts) / loserPts);
    const result = {
      side: winnerSide,
      name: winnerName,
      score: winnerPts,
      loser: isP1 ? (p2NameLocal || "Player 2") : p1Name,
      loserScore: loserPts,
      computedScore,
    };
    setWinner(result);

    wsSend({
      type: "game_over",
      winner_side: winnerSide,
      p1: s.p1.score,
      p2: s.p2.score,
      p1_name: p1Name,
      p2_name: p2NameLocal || "Player 2",
    });

    // Per user iter 61: persist to the Bong leaderboard
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/games/bong/leaderboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: winnerName.slice(0, 20),
          winner_points: Number(winnerPts) || 0,
          loser_points: Number(loserPts) || 0,
          difficulty,
        }),
      });
    } catch {/* leaderboard best-effort */}
  }, [p1Name, wsSend, difficulty]);

  // ---------------- Room creation + WebSocket ----------------
  const [wsAlive, setWsAlive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let createdCode = null;
    let reconnectAttempts = 0;
    // Iter 137: guard against the runaway room-reissue loop the user
    // reported (room key kept being recreated every minute or two
    // after the host issued one).  Allow exactly ONE automatic
    // room re-create on a "room_not_found" error; any further
    // occurrences surface as a soft error instead of recursing.
    let recoveryUsed = false;

    const handlers = {
      onmessage: (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "ping" || m.type === "pong") return;
        if (m.type === "controller_connected") {
          setControllerConnected(true);
        } else if (m.type === "controller_disconnected") {
          setControllerConnected(false);
          setP2Name(null);
          if (stateRef.current && stateRef.current.running && !stateRef.current.finished) {
            stateRef.current.paused = true;
            setPaused(true);
          }
        } else if (m.type === "join") {
          const nm = (m.name || "Player 2").slice(0, 20);
          const av = (m.avatar || "").slice(0, 60);
          setP2Name(nm);
          setP2Avatar(av);
          setControllerConnected(true);
          // Echo back our avatar so the controller can render it.
          wsSend({ type: "host_info", name: p1Name, avatar: p1Avatar });
        } else if (m.type === "error" && m.error === "room_not_found") {
          // Backend lost the room (e.g., dev hot-reload between createRoom and
          // WS connect). Spin up a fresh room and reconnect with the new key.
          // Guarded so a server that keeps replying "room_not_found" doesn't
          // make us bake a fresh key on every reconnect attempt.
          if (recoveryUsed) {
            setRoomError("Lost connection to the room.  Please refresh to host a new match.");
            return;
          }
          recoveryUsed = true;
          (async () => {
            try {
              if (ws) ws.close();
              const r = await createRoom({ p1_name: p1Name, difficulty });
              if (cancelled) return;
              createdCode = r.code;
              setRoomCode(r.code);
              connect(r.code);
            } catch (err) {
              setRoomError(err?.response?.data?.detail || err?.message || "Failed to recover room");
            }
          })();
        } else if (m.type === "input") {
          if (m.action === "up") p2DirRef.current = -1;
          else if (m.action === "down") p2DirRef.current = 1;
          else p2DirRef.current = 0;
        } else if (m.type === "chat") {
          const txt = String(m.text || "");
          if (!txt) return;
          setChatMessages((prev) => [
            ...prev.slice(-199),
            { from: m.from || "controller", name: (m.name || "Player 2").slice(0, 20), avatar: (m.avatar || "").slice(0, 60), text: txt, ts: Date.now() },
          ]);
        }
      },
    };

    const connect = (code) => {
      if (cancelled) return;
      ws = new WebSocket(`${wsBase()}/ws/room/${code}/host`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsAlive(true);
        reconnectAttempts = 0;
        flushOutbound();
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: "ping", t: Date.now() })); } catch {}
          }
        }, 10000);
      };
      ws.onmessage = handlers.onmessage;
      ws.onerror = () => {};
      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (cancelled) return;
        setWsAlive(false);
        setControllerConnected(false);
        const delay = Math.min(5000, 500 * Math.pow(2, reconnectAttempts));
        reconnectAttempts += 1;
        if (reconnectAttempts <= 30) {
          reconnectTimer = setTimeout(() => connect(code), delay);
        }
      };
    };

    (async () => {
      let lastErr;
      // 6 attempts w/ exponential backoff (0.5s,1s,2s,4s,8s,16s ≈ 31s budget)
      // — survives a brief dev-mode backend reload without stranding the
      // host on "Preparing the lists…".
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          const { code } = await createRoom({ p1_name: p1Name, difficulty });
          if (cancelled) return;
          createdCode = code;
          setRoomCode(code);
          setRoomError(null);
          connect(code);
          return;
        } catch (e) {
          lastErr = e;
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
      }
      if (!cancelled) {
        setRoomError(lastErr?.response?.data?.detail || lastErr?.message || "Failed to create room");
      }
    })();

    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) ws.close();
      wsRef.current = null;
    };
  }, [difficulty, p1Name, flushOutbound]);

  useEffect(() => {
    wsSend({ type: "score", p1: score.p1, p2: score.p2 });
  }, [score, wsSend]);

  const startMatch = useCallback(() => {
    if (!controllerConnected || !p2Name) return;
    if (matchStarted) return;
    setMatchStarted(true);
    startTimeRef.current = Date.now();
    setCountdown(3);
    let n = 3;
    const tickDown = () => {
      n -= 1;
      if (n <= 0) {
        setCountdown(0);
        const s = stateRef.current;
        s.paused = false;
        s.running = true;
        // Re-anchor the first bong-pipe event so it doesn't fire instantly
        // when the user has been sitting in the lobby for a while.
        s.nextBongPipeAt = Date.now() + BONG_PIPE_INTERVAL_MS;
        setPaused(false);
        wsSend({ type: "match_started" });
      } else {
        setCountdown(n);
        setTimeout(tickDown, 1000);
      }
    };
    setTimeout(tickDown, 1000);
  }, [controllerConnected, p2Name, matchStarted, wsSend]);

  const sendChat = useCallback((text) => {
    const safe = String(text || "");
    if (!safe.trim()) return;
    setChatMessages((prev) => [
      ...prev.slice(-199),
      { from: "host", name: p1Name, avatar: p1Avatar, text: safe, ts: Date.now() },
    ]);
    wsSendChat({ type: "chat", from: "host", name: p1Name, avatar: p1Avatar, text: safe });
  }, [p1Name, p1Avatar, wsSendChat]);

  useEffect(() => {
    if (matchStarted || winner) return;
    const id = requestAnimationFrame(() => {
      if (lobbyBottomRef.current && typeof lobbyBottomRef.current.scrollIntoView === "function") {
        lobbyBottomRef.current.scrollIntoView({ block: "end", inline: "nearest", behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [chatMessages.length, p2Name, matchStarted, winner]);

  // ---------------- Game loop ----------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const tick = () => {
      try {
      const s = stateRef.current;

      // Stream gameplay state to controller at ~60 fps — only while match is running.
      const nowStream = Date.now();
      if (s.running && !s.finished && nowStream - lastStreamRef.current >= 16) {
        lastStreamRef.current = nowStream;
        const wsLive = wsRef.current;
        if (wsLive && wsLive.readyState === WebSocket.OPEN) {
          wsLive.send(JSON.stringify({
            type: "tick",
            p1y: Math.round(s.p1.y),
            p1h: Math.round(s.p1.h),
            p2y: Math.round(s.p2.y),
            p2h: Math.round(s.p2.h),
            p1a: !!s.p1.aegis,
            p2a: !!s.p2.aegis,
            bs: s.balls.map((b) => ({ x: Math.round(b.x), y: Math.round(b.y), s: b.scoring ? 1 : 0 })),
            pu: s.powerup ? { x: Math.round(s.powerup.x), y: Math.round(s.powerup.y), t: s.powerup.type } : null,
            run: s.running && !s.paused && !s.finished,
          }));
        }
      }

      if (!s.paused && !s.finished && s.running) {
        const now = Date.now();
        [s.p1, s.p2].forEach((p) => {
          if (p.expiresAt && now > p.expiresAt) {
            p.h = BASE_PADDLE_H;
            p.expiresAt = 0;
          }
        });

        const keys = keysRef.current;
        const ps = cfg.paddleSpeed;
        if (keys["w"] || keys["W"] || keys["ArrowUp"]) s.p1.y -= ps;
        if (keys["s"] || keys["S"] || keys["ArrowDown"]) s.p1.y += ps;
        if (p2DirRef.current === -1) s.p2.y -= ps;
        else if (p2DirRef.current === 1) s.p2.y += ps;
        s.p1.y = clamp(s.p1.y, 0, H - s.p1.h);
        s.p2.y = clamp(s.p2.y, 0, H - s.p2.h);

        // Per-ball physics
        const surviving = [];
        for (const b of s.balls) {
          b.x += b.vx;
          b.y += b.vy;

          if (b.y - b.r < 0) { b.y = b.r; b.vy = Math.abs(b.vy); sfx.wall(); }
          else if (b.y + b.r > H) { b.y = H - b.r; b.vy = -Math.abs(b.vy); sfx.wall(); }

          // P1 paddle
          {
            const p = s.p1;
            const withinY = b.y + b.r > p.y && b.y - b.r < p.y + p.h;
            if (b.x - b.r < p.x + p.w && b.x > p.x && withinY && b.vx < 0) {
              b.x = p.x + p.w + b.r;
              const rel = (b.y - (p.y + p.h / 2)) / (p.h / 2);
              const angle = rel * (Math.PI / 4);
              b.speed = Math.min(b.speed * 1.04, cfg.ballSpeed * 2.4);
              b.vx = Math.cos(angle) * b.speed;
              b.vy = Math.sin(angle) * b.speed;
              s.lastHitter = "p1"; sfx.paddle();
            }
          }
          // P2 paddle
          {
            const p = s.p2;
            const withinY = b.y + b.r > p.y && b.y - b.r < p.y + p.h;
            if (b.x + b.r > p.x && b.x < p.x + p.w && withinY && b.vx > 0) {
              b.x = p.x - b.r;
              const rel = (b.y - (p.y + p.h / 2)) / (p.h / 2);
              const angle = rel * (Math.PI / 4);
              b.speed = Math.min(b.speed * 1.04, cfg.ballSpeed * 2.4);
              b.vx = -Math.cos(angle) * b.speed;
              b.vy = Math.sin(angle) * b.speed;
              s.lastHitter = "p2"; sfx.paddle();
            }
          }

          // (Ball no longer triggers surprise drops — see below for the
          // new paddle-vs-drop collision check.)

          surviving.push(b);
        }
        s.balls = surviving;

        if (!s.powerup && now >= s.nextPowerupAt) spawnPowerup();

        // ----- Move active surprise drop horizontally -----
        if (s.powerup) {
          s.powerup.x += s.powerup.vx || 0;

          // ----- Surprise drop vs PADDLE collision -----
          // When the drop touches either paddle, apply its effect to the
          // OTHER side's ball trajectory and remove the drop.  This is the
          // intended pickup gesture now: paddles "catch" surprise drops.
          const hitsRect = (px, py, r, rx, ry, rw, rh) => (
            px + r > rx && px - r < rx + rw &&
            py + r > ry && py - r < ry + rh
          );
          let caughtBy = null;
          if (hitsRect(s.powerup.x, s.powerup.y, POWERUP_R, s.p1.x, s.p1.y, s.p1.w, s.p1.h)) {
            caughtBy = "p1";
          } else if (hitsRect(s.powerup.x, s.powerup.y, POWERUP_R, s.p2.x, s.p2.y, s.p2.w, s.p2.h)) {
            caughtBy = "p2";
          }
          if (caughtBy) {
            applyPowerup(s.powerup.type, caughtBy);
            // applyPowerup already nulls s.powerup and sets nextPowerupAt.
          } else if (s.powerup && (s.powerup.x < -POWERUP_R - 10 || s.powerup.x > W + POWERUP_R + 10)) {
            // Despawn drop that flew off either side; schedule the next one.
            s.powerup = null;
            s.nextPowerupAt = now + cfg.powerInterval;
          }
        }

        // ----- Bong-pipe periodic event -----
        // Spawn a glass bong pipe on a random player's side that lingers
        // for BONG_PIPE_VISIBLE_MS milliseconds, accompanied by a gurgle.
        if (!s.bongPipe && now >= s.nextBongPipeAt) {
          const side = Math.random() < 0.5 ? "p1" : "p2";
          const xJitter = 40 + Math.random() * 80;
          const yJitter = 80 + Math.random() * (H - 200);
          s.bongPipe = {
            side,
            x: side === "p1" ? xJitter + 120 : W - xJitter - 120,
            y: yJitter,
            until: now + BONG_PIPE_VISIBLE_MS,
          };
          s.nextBongPipeAt = now + BONG_PIPE_INTERVAL_MS + Math.random() * 4000;
          // Audio call is best-effort — Safari may throw if the AudioContext
          // can't resume; never let that crash the game loop.
          try { sfx.bubble(); } catch (e) { /* swallow */ }
        }
        if (s.bongPipe && now >= s.bongPipe.until) {
          s.bongPipe = null;
        }

        // Score / off-screen handling — process scoring ball first
        const scoringIdx = s.balls.findIndex((b) => b.scoring);
        if (scoringIdx >= 0) {
          const sb = s.balls[scoringIdx];
          if (sb.x < -20) {
            // P2 was about to score on P1
            if (s.p1.aegis) {
              s.p1.aegis = false;
              sfx.powerup();
              sb.x = s.p1.x + s.p1.w + sb.r + 4;
              sb.y = clamp(sb.y, 20, H - 20);
              sb.vx = Math.abs(sb.vx);
            } else {
              s.p2.score += 1;
              setScore({ p1: s.p1.score, p2: s.p2.score });
              setScorePulse((p) => ({ ...p, p2: p.p2 + 1 }));
              sfx.score();
              s.p1.h = BASE_PADDLE_H; s.p1.expiresAt = 0; s.p1.aegis = false;
              s.p2.h = BASE_PADDLE_H; s.p2.expiresAt = 0; s.p2.aegis = false;
              s.powerup = null; s.nextPowerupAt = Date.now() + cfg.powerInterval;
              if (s.p2.score >= cfg.winScore) finishMatch("p2", p2Name);
              else resetBalls("p1");
            }
          } else if (sb.x > W + 20) {
            if (s.p2.aegis) {
              s.p2.aegis = false;
              sfx.powerup();
              sb.x = s.p2.x - sb.r - 4;
              sb.y = clamp(sb.y, 20, H - 20);
              sb.vx = -Math.abs(sb.vx);
            } else {
              s.p1.score += 1;
              setScore({ p1: s.p1.score, p2: s.p2.score });
              setScorePulse((p) => ({ ...p, p1: p.p1 + 1 }));
              sfx.score();
              s.p1.h = BASE_PADDLE_H; s.p1.expiresAt = 0; s.p1.aegis = false;
              s.p2.h = BASE_PADDLE_H; s.p2.expiresAt = 0; s.p2.aegis = false;
              s.powerup = null; s.nextPowerupAt = Date.now() + cfg.powerInterval;
              if (s.p1.score >= cfg.winScore) finishMatch("p1", p2Name);
              else resetBalls("p2");
            }
          }
        }

        // Drop non-scoring balls that flew off-screen
        s.balls = s.balls.filter((b) => b.scoring || (b.x > -40 && b.x < W + 40));
      }

      // Draw
      ctx.clearRect(0, 0, W, H);
      drawArenaBackground(ctx);

      // P1 paddle (neon blue) with aegis aura
      drawPaddle(ctx, s.p1.x, s.p1.y, s.p1.w, s.p1.h, "#3B82F6", s.p1.aegis);
      // P2 paddle (neon red) with aegis aura
      drawPaddle(ctx, s.p2.x, s.p2.y, s.p2.w, s.p2.h, "#EF4444", s.p2.aegis);

      // Lacquered wooden balls — no glow
      for (const b of s.balls) {
        drawBall(ctx, b.x, b.y, b.r, b.scoring);
      }

      if (s.powerup) {
        const def = POWERUP_DEFS[s.powerup.type] || POWERUP_DEFS.fast;
        const pulse = 1 + Math.sin(Date.now() / 180) * 0.12;
        ctx.shadowColor = def.glow; ctx.shadowBlur = 24; ctx.fillStyle = def.fill;
        ctx.beginPath(); ctx.arc(s.powerup.x, s.powerup.y, POWERUP_R * pulse, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
        // Purple-neon rim instead of gold
        ctx.strokeStyle = "#C084FC"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(s.powerup.x, s.powerup.y, POWERUP_R * pulse, 0, Math.PI * 2); ctx.stroke();
        drawPowerupIcon(ctx, s.powerup.type, s.powerup.x, s.powerup.y);
      }

      // Periodic bong-pipe — pulsing glass with rising bubbles
      if (s.bongPipe) {
        const t = (s.bongPipe.until - Date.now()) / BONG_PIPE_VISIBLE_MS;
        const pulse = 1 + Math.sin(Date.now() / 130) * 0.06;
        ctx.save();
        ctx.shadowColor = "#C084FC";
        ctx.shadowBlur = 22;
        ctx.globalAlpha = Math.min(1, Math.max(0.35, t + 0.35));
        drawBongPipe(ctx, s.bongPipe.x, s.bongPipe.y, 1.8 * pulse);
        ctx.restore();
      }
      ctx.shadowBlur = 0;

      } catch (err) {
        // Defensive: never let a single bad frame kill the rAF chain.
        // (Safari AudioContext / canvas-state issues can throw inside the
        // tick; we log but keep scheduling new frames so the match
        // continues to render.)
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[Bong] tick error (continuing):", err);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cfg.ballSpeed, cfg.paddleSpeed, cfg.powerInterval, applyPowerup, finishMatch, resetBalls, spawnPowerup, p2Name]);

  // P1 keyboard
  useEffect(() => {
    const isTyping = (e) => {
      const t = e.target;
      if (!t) return false;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable;
    };
    const down = (e) => {
      if (isTyping(e)) return;
      if (["w", "W", "s", "S", " ", "ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault();
      if (e.key === " ") {
        if (!matchStarted || winner) return;
        setPaused((p) => {
          const next = !p;
          if (stateRef.current) stateRef.current.paused = next;
          return next;
        });
        return;
      }
      keysRef.current[e.key] = true;
    };
    const up = (e) => {
      if (isTyping(e)) return;
      keysRef.current[e.key] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [matchStarted, winner]);

  const togglePause = () => {
    if (!matchStarted || winner) return;
    setPaused((p) => {
      const next = !p;
      if (stateRef.current) stateRef.current.paused = next;
      return next;
    });
  };

  const controllerUrl = `${window.location.origin}/controller${roomCode ? `?code=${roomCode}` : ""}`;

  return (
    <div className="wupong-stage relative min-h-screen overflow-hidden flex flex-col wupong-bg" data-testid="pong-game-page">
      <div className="wupong-grain pointer-events-none absolute inset-0" />

      {/* Player 2 corner card — top right of the page */}
      {matchStarted && p2Avatar && (
        <div
          className="flex items-center gap-2 arcade-panel !px-3 !py-2 shadow-lg"
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 30 }}
          data-testid="player2-corner"
        >
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase text-blue-700 font-display truncate max-w-[140px]">
              {p2Name || "Offense"}
            </div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-stone-600 font-display">
              Offense
            </div>
          </div>
          <img
            src={resolveAvatarUrl(p2Avatar)}
            alt={p2Name}
            className="w-12 h-14 object-cover rounded-sm border-2 border-blue-500/70 bg-stone-800 shrink-0"
            style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }}
            data-testid="player2-avatar"
          />
        </div>
      )}

      {/* Player 1 corner card — bottom left of the page */}
      {matchStarted && p1Avatar && (
        <div
          className="flex items-center gap-2 arcade-panel !px-3 !py-2 shadow-lg"
          style={{ position: "fixed", bottom: "1rem", left: "1rem", zIndex: 30 }}
          data-testid="player1-corner"
        >
          <img
            src={resolveAvatarUrl(p1Avatar)}
            alt={p1Name}
            className="w-12 h-14 object-cover rounded-sm border-2 border-red-500/70 bg-stone-800 shrink-0"
            style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }}
            data-testid="player1-avatar"
          />
          <div className="text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-700 font-display truncate max-w-[140px]">
              {p1Name}
            </div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-stone-600 font-display">
              Defense
            </div>
          </div>
        </div>
      )}

      <TopBar
        theme="wupong"
        onBack={() => navigate("/pong-tournament")}
        backLabel="EXIT"
        testId="exit-game-button"
        center={
          <div className="wupong-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-2 md:gap-3 flex-wrap justify-center" style={{ color: "#C084FC" }}>
            <span className="whitespace-nowrap">VETERAN · TO {cfg.winScore}</span>
            {!wsAlive ? (
              <span className="flex items-center gap-1 whitespace-nowrap" style={{ color: "#6D28D9" }} data-testid="connection-indicator-dead">
                <WifiOff size={12} /> SIGNAL LOST
              </span>
            ) : controllerConnected ? (
              <span className="flex items-center gap-1 whitespace-nowrap" style={{ color: "#86EFAC" }} data-testid="connection-indicator-on">
                <Wifi size={12} /> OFFENSE LIVE
              </span>
            ) : (
              <span className="flex items-center gap-1 whitespace-nowrap" style={{ color: "#6D28D9" }} data-testid="connection-indicator-off">
                <WifiOff size={12} /> AWAITING
              </span>
            )}
          </div>
        }
        right={
          <button onClick={togglePause} disabled={!matchStarted || !!winner} className="wupong-ghost text-xs disabled:opacity-40" data-testid="pause-button">
            {paused ? <><Play size={14} className="inline -mt-0.5 mr-1" /> RESUME</> : <><Pause size={14} className="inline -mt-0.5 mr-1" /> PAUSE</>}
          </button>
        }
      />

      <div className="relative z-10 max-w-6xl w-full mx-auto px-4 pt-20 md:pt-20 pb-10 flex-1 flex flex-col">
        {!wsAlive && (
          <div className="mb-4 arcade-panel px-4 py-3 text-center" data-testid="host-reconnect-banner">
            <div className="text-rose-400 font-display text-xs tracking-[0.3em] uppercase mb-2">
              The raven hath lost its way
            </div>
            <button onClick={() => window.location.reload()} className="arcade-btn arcade-btn-magenta !px-5 !py-2 text-xs" data-testid="host-reconnect-button">
              Reconnect
            </button>
          </div>
        )}

        <div className="grid grid-cols-3 items-end mb-4 gap-4">
          <div className="text-left">
            <div
              className="text-xs tracking-[0.3em] uppercase truncate"
              style={{ color: "#C084FC", fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive" }}
              data-testid="player1-display-name"
            >
              {p1Name} · Defense
            </div>
            <div
              key={`p1-${scorePulse.p1}`}
              className="font-bold text-5xl md:text-6xl score-ping"
              style={{
                color: "#C084FC",
                fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
                textShadow: "3px 3px 0 #6D28D9, 0 0 18px rgba(192,132,252,0.55)",
              }}
              data-testid="player1-score"
            >
              {(score.p1 / 1000).toFixed(3)}
            </div>
          </div>
          <div
            className="text-center tracking-[0.4em] uppercase text-xs pb-3"
            style={{ color: "#C084FC", fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive" }}
          >
            vs.
          </div>
          <div className="text-right">
            <div
              className="text-xs tracking-[0.3em] uppercase truncate"
              style={{ color: "#C084FC", fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive" }}
              data-testid="player2-display-name"
            >
              {p2Name ? `${p2Name} · Offense` : "Awaiting Offense"}
            </div>
            <div
              key={`p2-${scorePulse.p2}`}
              className="font-bold text-5xl md:text-6xl score-ping"
              style={{
                color: "#F0E6FF",
                fontFamily: "'Comic Sans MS','Comic Sans','Chalkboard SE',cursive",
                textShadow: "3px 3px 0 #6D28D9, 0 0 18px rgba(240,230,255,0.45)",
              }}
              data-testid="player2-score"
            >
              {(score.p2 / 1000).toFixed(3)}
            </div>
          </div>
        </div>

        <div className="canvas-wrap scanlines crt-flicker w-full" style={{ aspectRatio: `${W} / ${H}` }} data-testid="pong-canvas-wrap">
          <canvas ref={canvasRef} width={W} height={H} className="block w-full h-full" data-testid="pong-canvas" />

          {!matchStarted && !winner && (
            <div className="absolute inset-0 z-10 flex flex-col items-center bg-black/80 px-4 py-6 overflow-y-auto" data-testid="waiting-for-controller-overlay">
              <div className="arcade-panel px-6 py-6 max-w-lg w-full text-center my-auto">
                <Smartphone className="mx-auto text-blue-300 mb-2 neon-pulse" size={32} />
                <div className="text-xs tracking-[0.4em] text-red-700 font-display uppercase mb-2">Playroom Key</div>
                {roomCode ? (
                  <div className="font-display text-5xl md:text-6xl font-black tracking-[0.4em] mb-3 font-playfair" style={{ color: "#7A1F1F", textShadow: "0 1px 0 rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.3)" }} data-testid="room-code-display">
                    {roomCode}
                  </div>
                ) : roomError ? (
                  <div className="font-display text-xl text-red-700 mb-3" data-testid="room-code-display">
                    —
                  </div>
                ) : (
                  <div className="font-display text-xl text-stone-700 italic mb-3 animate-pulse" data-testid="room-code-display">
                    Preparing the lists…
                  </div>
                )}
                {roomError && (
                  <div className="mb-3" data-testid="room-error">
                    <div className="text-red-700 text-sm font-body italic mb-2">{roomError}</div>
                    <button
                      onClick={() => window.location.reload()}
                      className="arcade-btn arcade-btn-magenta !px-4 !py-2 text-xs"
                      data-testid="room-retry-button"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {!p2Name ? (
                  <>
                    <div className="text-sm text-stone-700 font-body italic mb-2">
                      Open the join page on another device at
                    </div>
                    <div
                      className="font-mono text-xs md:text-sm text-red-800 break-all border border-red-700/60 bg-amber-50/30 px-3 py-2 mb-3"
                      data-testid="controller-url"
                    >
                      {controllerUrl}
                    </div>
                    <div className="text-xs text-stone-700 font-body italic" data-testid="waiting-status">
                      {controllerConnected ? "Offense connecting…" : "Waiting for Offense to join…"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-emerald-800 font-body italic mb-3" data-testid="lobby-ready-status">
                      ⚜ {p2Name} hath taken the field — parley, then sound the horn
                    </div>

                    <ChatPanel
                      messages={chatMessages}
                      onSend={sendChat}
                      selfRole="host"
                      selfName={p1Name}
                      testIdPrefix="host-chat"
                      className="mb-4 text-left"
                    />

                    <button
                      onClick={startMatch}
                      disabled={!controllerConnected}
                      className="arcade-btn !px-10 !py-3 disabled:opacity-50"
                      data-testid="start-match-button"
                    >
                      <DaggerInWoodIcon size={20} /> Sound the Horn
                    </button>

                    <div ref={lobbyBottomRef} aria-hidden="true" className="h-1" />
                  </>
                )}
              </div>
            </div>
          )}

          {matchStarted && countdown > 0 && !winner && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 pointer-events-none" data-testid="countdown-overlay">
              <div className="font-display text-9xl font-black text-glow-cyan score-ping" key={countdown}>
                {countdown}
              </div>
            </div>
          )}

          {paused && matchStarted && !winner && countdown === 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60" data-testid="pause-overlay">
              <div className="arcade-panel px-10 py-8 text-center">
                <div className="text-xs tracking-[0.4em] text-red-700 font-display uppercase mb-2">Paused</div>
                <div className="text-3xl font-display font-bold text-glow-cyan">PRESS SPACE</div>
                <div className="mt-4 text-sm text-stone-700 font-body">
                  {!controllerConnected ? "Player 2 disconnected — match held." : "or click Resume"}
                </div>
              </div>
            </div>
          )}

          {winner && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/55 px-4" data-testid="game-over-overlay">
              <div className="arcade-panel px-8 py-8 max-w-md w-full text-center">
                <Trophy className="mx-auto text-yellow-600 mb-2 neon-pulse" size={42} />
                <div className="text-xs tracking-[0.4em] text-amber-800 font-display uppercase mb-1">Hail the Victor</div>
                <div
                  className={`font-display text-3xl md:text-4xl font-black uppercase mb-3 ${winner.side === "p1" ? "text-glow-cyan" : "text-glow-magenta"}`}
                  data-testid="winner-name"
                >
                  {winner.name}
                </div>
                <div className="font-mono text-xl text-stone-900 mb-2" data-testid="final-score">
                  {winner.score} <span className="text-stone-500">–</span> {winner.loserScore}
                </div>
                <div
                  className="font-display text-base text-amber-800 uppercase tracking-[0.2em] mb-6"
                  data-testid="bong-score-earned"
                >
                  Score earned · {Number(winner.computedScore || 0).toLocaleString()}
                </div>

                <div className="flex justify-center">
                  <button className="arcade-btn arcade-btn-magenta !py-3 !px-8" onClick={() => navigate("/pong-tournament")} data-testid="play-again-button">
                    Return to Bong
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-xs text-amber-200/60 font-mono tracking-widest">
          SPACE — call a halt · Joust to first {cfg.winScore}
        </div>
      </div>
    </div>
  );
};

export default PongGame;
